/**
 * AnalysisCoverage — NMW-1170 (parent NMW-1164).
 *
 * Makes analysis coverage legible WITHOUT opening an asset:
 *
 *   1. `<AssetCoverageBadge assetId>` — a per-row pill in the library table.
 *   2. `<FleetCoverageStrip>`        — real fleet counts (enriched / pending /
 *                                      failed / never analysed).
 *   3. `<AssetCoverageRow assetId>`  — the same state, plus the failure reason,
 *                                      on the asset-detail "Procesamiento"
 *                                      panel.
 *
 * READ-ONLY BY DESIGN. There is deliberately NO select-and-dispatch control
 * here, not even a disabled one: bulk re-analysis has to queue through the
 * NMW-1149 cluster admission cap, which is a different milestone. The shape
 * below makes adding it later cheap (the provider already holds the visible
 * page's coverage keyed by asset id) without shipping a button that lies about
 * what the backend will accept.
 *
 * ── THE N+1 THIS AVOIDS ─────────────────────────────────────────────────────
 * The obvious implementation is `useQuery(getCoverageForAsset, { assetId })`
 * inside the row component. React's rules of hooks permit that (one hook per
 * component instance, not a loop), so it would compile — and it would open one
 * live Convex subscription PER VISIBLE ROW, 25 of them on the first page of the
 * library, each re-running on every `analysisJobs` write. Unit 05 shipped
 * `getCoverageForAssets` (a bounded batch, `convex/analysis/queries.ts`)
 * precisely so this surface issues ONE subscription for the whole page.
 *
 * Getting a single batched result down to leaf cells needs a way to fan the
 * answer out, and the query must not be able to blank the screen when it
 * throws. Hence the shape:
 *
 *   <AssetCoverageProvider assetIds={visible}>   ← holds state + context
 *     <CoverageBoundary>                          ← catches the render throw
 *       <CoverageFetch/>  (renders null)          ← the ONE useQuery
 *     </CoverageBoundary>
 *     …table…  <AssetCoverageBadge assetId/>      ← context consumers, always
 *   </AssetCoverageProvider>                        rendered, never suspended
 *
 * The fetch component publishes upward through an effect — the same
 * one-hook-child idiom `FolderChildrenProbe` already uses in `_bundle.tsx`.
 * Because the consumers sit OUTSIDE the boundary, a failed coverage read costs
 * the coverage column and nothing else: the library table still renders.
 *
 * ── HONEST STATES (constitution: honest-state contract) ─────────────────────
 *   undefined        → skeleton pill. Not "sin analizar".
 *   error / throw    → "Cobertura no disponible" + one error toast per id set.
 *                      A read failure must NEVER render as "not analysed" —
 *                      that is the fabricated-result shape the rule forbids.
 *   over the cap     → "Cobertura no disponible" for the overflow ids. The
 *                      backend THROWS above COVERAGE_BATCH_MAX rather than
 *                      truncating, so we never send more than it accepts and
 *                      we never silently drop ids either.
 *   asset deleted    → `exists: false` from the backend → "Sin datos".
 *   unknown state    → fails CLOSED to "Estado desconocido". A state literal
 *                      this UI has not been taught is not "sin analizar".
 *
 * A FAILED analysis is never shown as an unattempted one: `failed` gets its own
 * tone and label, and it carries `analysisJobs.failureReason` (pod-level
 * reasons like `OOMKilled` land there once NMW-1151 ships) as visible text —
 * on the badge's tooltip in the dense table, and as a full line on the detail
 * panel and in the fleet strip's breakdown.
 *
 * An asset that FAILED once and later SUCCEEDED still reports the failure
 * (`latestFailure` is independent of `state`, per unit 05) — the strip counts
 * those under "con fallos" separately from the `failed` state count, and says
 * so, because the two numbers are legitimately different and collapsing them
 * would misreport one of them.
 */
import React, { Component, createContext, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useQuery } from 'convex/react';
import { api } from '@convex/api';
import { EmptyState, Panel, Pill, Skeleton, useToast } from '../../components/primitives';

/* ------------------------------------------------------------------ *
 * Contract with convex/analysis/queries.ts (unit 05, NMW-1169)
 * ------------------------------------------------------------------ */

/**
 * Mirrors `COVERAGE_BATCH_MAX` in `convex/analysis/queries.ts`. Exceeding it
 * makes the backend THROW (deliberately — a short list would read as "these
 * assets were never analysed"), so the provider slices at this number and
 * reports the remainder as unavailable rather than as un-analysed.
 */
export const COVERAGE_BATCH_MAX = 200;

export type AssetCoverageState =
  | 'not_analysed'
  | 'queued'
  | 'analysing'
  | 'stage1_complete'
  | 'enriched'
  | 'failed';

export interface CoverageFailure {
  jobId: string;
  failureReason: string | null;
  error: string | null;
  at: number;
}

export interface AssetCoverage {
  assetId: string;
  state: AssetCoverageState | string;
  attemptCount: number;
  latestJobId: string | null;
  latestJobStatus: string | null;
  latestJobState: string | null;
  progress: number | null;
  manifestReady: boolean;
  hasEnrichment: boolean;
  /**
   * NMW-1214 — the asset has at least one enrichment row `search_content` can
   * actually return (non-empty `searchText`, pinned `tenantId`). STRICTLY
   * narrower than `hasEnrichment`.
   *
   * Optional on purpose: a backend that predates NMW-1214 does not send it,
   * and `undefined` means UNKNOWN. It is never coerced to `true` (that is the
   * overclaim) nor to `false` (that would invent a failure).
   */
  isSearchable?: boolean;
  /** The bounded probe ran out of rows — `isSearchable: false` is a floor. */
  searchProbeTruncated?: boolean;
  completedAt: number | null;
  latestFailure: CoverageFailure | null;
  /**
   * Card MA-82 (CONTRACTS.md section 16.1, additive): the four modality words
   * of the asset's CURRENT v2 run, and that run's id. `null` (or absent, from a
   * backend that predates the card) for an asset covered by its v1 jobs — a v1
   * analysis has no modalities, so the single state pill stands and no word is
   * invented for it (DEF-15).
   */
  modalities?: RunModalities | null;
  currentRunId?: string | null;
}

/* ------------------------------------------------------------------ *
 * The four modalities of a v2 run (CONTRACTS.md section 9.10, 16.2; MA-82)
 * ------------------------------------------------------------------ */

export type ModalityWord = 'pending' | 'completed' | 'skipped' | 'failed';
export type ModalityName = 'frames' | 'transcript' | 'captions' | 'index';
/** `mediaAnalysisRuns.modalities` as the coverage query copies it. A word this build does not know is kept as text. */
export type RunModalities = Record<ModalityName, ModalityWord | string>;

export const MODALITY_ORDER: ModalityName[] = ['frames', 'transcript', 'captions', 'index'];

export const MODALITY_LABELS: Record<ModalityName, string> = {
  frames: 'Fotogramas',
  transcript: 'Transcripción',
  captions: 'Subtítulos',
  index: 'Índice',
};

/** The console's words for the four states (section 16.2 "Words on screen"). */
const MODALITY_WORDS: Record<ModalityWord, { label: string; tone: Presentation['tone'] }> = {
  pending: { label: 'pendiente', tone: 'neutral' },
  completed: { label: 'completado', tone: 'good' },
  skipped: { label: 'omitido', tone: 'info' },
  failed: { label: 'fallido', tone: 'bad' },
};

/**
 * Fail-closed: a word this build does not know is shown as unknown, never as
 * "completado" — the same rule as `coveragePresentation`.
 */
export function modalityWord(word: string): { label: string; tone: Presentation['tone'] } {
  return MODALITY_WORDS[word as ModalityWord] ?? { label: 'desconocido', tone: 'neutral' };
}

/** The four chips, one per modality, in a fixed order. Pure presentation. */
export function ModalityChips({ modalities }: { modalities: RunModalities }): React.ReactElement {
  return (
    <span data-testid="coverage-modalities" style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 4 }}>
      {MODALITY_ORDER.map((name) => {
        const word = String(modalities[name]);
        const w = modalityWord(word);
        return (
          <span
            key={name}
            data-testid={`coverage-modality-${name}`}
            data-word={word}
            title={`${MODALITY_LABELS[name]}: ${w.label}`}
          >
            <Pill tone={w.tone} size="sm">{MODALITY_LABELS[name]} · {w.label}</Pill>
          </span>
        );
      })}
    </span>
  );
}

export interface CoverageRow {
  assetId: string;
  exists: boolean;
  title: string | null;
  trashedAt: number | null;
  coverage: AssetCoverage | null;
}

export interface FleetCoverage {
  window: number;
  scannedAssets: number;
  truncated: boolean;
  counts: Record<string, number>;
  /**
   * NMW-1214 — assets content search can actually find. Always <=
   * `counts.enriched`. `undefined` = a backend that does not report it, which
   * is rendered as unknown, never as "all of them".
   */
  searchableAssets?: number;
  enrichedNotSearchable?: number;
  searchProbeTruncatedAssets?: number;
  assetsWithAnyFailure: number;
  failureReasons: Array<{ reason: string; count: number }>;
}

/* ------------------------------------------------------------------ *
 * Presentation vocabulary
 * ------------------------------------------------------------------ */

interface Presentation {
  label: string;
  tone: 'good' | 'warn' | 'bad' | 'info' | 'neutral';
  /** Long form for the detail panel and the badge tooltip. */
  hint: string;
}

const PRESENTATION: Record<AssetCoverageState, Presentation> = {
  not_analysed: {
    label: 'Sin analizar',
    tone: 'neutral',
    hint: 'Nunca se lanzó un análisis para este video. No aparece en la búsqueda por diálogo.',
  },
  queued: {
    label: 'En cola',
    tone: 'info',
    hint: 'Esperando un cupo en el clúster de análisis.',
  },
  analysing: {
    label: 'Analizando',
    tone: 'warn',
    hint: 'El análisis está en curso.',
  },
  stage1_complete: {
    label: 'Escenas listas',
    tone: 'info',
    hint: 'Se detectaron las escenas, pero todavía no hay transcripción ni descripción visual: no aparece en la búsqueda por diálogo.',
  },
  enriched: {
    label: 'Enriquecido',
    tone: 'good',
    // NMW-1214: "enriquecido" does NOT imply "buscable" — an enrichment row
    // with no indexed text is invisible to content search. The searchability
    // sentence is appended by `searchabilityNote`, which knows the answer.
    hint: 'Tiene transcripción y descripción visual.',
  },
  failed: {
    label: 'Falló',
    tone: 'bad',
    hint: 'El último intento de análisis falló. NO es lo mismo que un video sin analizar.',
  },
};

/**
 * Fail-closed: a state literal this build does not know is reported as unknown,
 * never folded into "sin analizar". A permissive default here would hide a new
 * backend state behind a wrong, reassuring label.
 */
export function coveragePresentation(state: string): Presentation {
  return (
    PRESENTATION[state as AssetCoverageState] ?? {
      label: 'Estado desconocido',
      tone: 'neutral',
      hint: `El backend informó el estado "${state}", que esta interfaz no reconoce.`,
    }
  );
}

/** The reason a failure is shown with — never invented, only ever reported. */
export function failureText(failure: CoverageFailure | null | undefined): string | null {
  if (!failure) return null;
  const reason = failure.failureReason?.trim();
  const detail = failure.error?.trim();
  if (reason && detail && detail !== reason) return `${reason} — ${detail}`;
  return reason || detail || 'El backend no registró un motivo.';
}

/* ------------------------------------------------------------------ *
 * Searchability (NMW-1214)
 * ------------------------------------------------------------------ *
 * "Analizado" and "buscable" are different populations, and the second is
 * smaller. An `enrichmentResults` row with no `searchText` (rows predating
 * NMW-1167, rows from a writer that does not derive it) is real enrichment
 * that `search_content` can never return. Reporting the larger number as the
 * searchable one is exactly the fabricated result this surface exists to
 * prevent, so the two are shown separately whenever they diverge.
 */

/**
 * The searchability sentence for one asset. `null` when the state says nothing
 * about searchability (nothing was analysed yet), so no sentence is invented.
 */
export function searchabilityNote(
  coverage: Pick<AssetCoverage, 'state' | 'hasEnrichment' | 'isSearchable' | 'searchProbeTruncated'>,
): string | null {
  if (!coverage.hasEnrichment && coverage.state !== 'enriched') return null;
  if (coverage.isSearchable === true) {
    return 'Es buscable por contenido: tiene texto indexado.';
  }
  if (coverage.isSearchable === false) {
    return coverage.searchProbeTruncated
      ? 'No se pudo confirmar si es buscable por contenido: solo se revisaron las primeras filas de enriquecimiento.'
      : 'NO es buscable por contenido: su análisis no dejó texto indexado, así que la búsqueda por diálogo no lo devuelve.';
  }
  // undefined — the backend did not say. Unknown is reported as unknown.
  return 'No se sabe si es buscable por contenido: el backend no informó ese dato.';
}

/**
 * Presentation for an asset, searchability included. Only `enriched` changes:
 * an enriched-but-not-searchable asset must not wear the same reassuring pill
 * as one the search can actually find.
 */
export function coveragePresentationFor(
  coverage: Pick<AssetCoverage, 'state' | 'hasEnrichment' | 'isSearchable' | 'searchProbeTruncated'>,
): Presentation {
  const base = coveragePresentation(coverage.state);
  const note = searchabilityNote(coverage);
  const hint = note ? `${base.hint} ${note}` : base.hint;
  if (coverage.state !== 'enriched') return { ...base, hint };
  if (coverage.isSearchable === true) return { ...base, hint };
  if (coverage.isSearchable === false && !coverage.searchProbeTruncated) {
    return { label: 'Enriquecido, no buscable', tone: 'warn', hint };
  }
  // Unknown / unconfirmed: not claimed as searchable, not reported as broken.
  return { label: 'Enriquecido', tone: 'info', hint };
}

/* ------------------------------------------------------------------ *
 * Provider: one batched subscription for the visible page
 * ------------------------------------------------------------------ */

type CoverageMap = Map<string, CoverageRow>;

interface CoverageContextValue {
  /** `undefined` = still loading. `null` = the read FAILED (never "empty"). */
  byAsset: CoverageMap | undefined | null;
  /** Ids beyond COVERAGE_BATCH_MAX — not queried, and honestly marked so. */
  overflow: Set<string>;
}

const CoverageContext = createContext<CoverageContextValue>({
  byAsset: undefined,
  overflow: new Set(),
});

/**
 * The single `useQuery`. Renders nothing; publishes its result upward so the
 * consumers can live outside the error boundary that wraps this.
 */
function CoverageFetch({
  assetIds,
  onRows,
}: {
  assetIds: string[];
  onRows: (rows: CoverageRow[]) => void;
}): null {
  const rows = useQuery(
    api.analysis.queries.getCoverageForAssets,
    assetIds.length > 0 ? ({ assetIds } as never) : 'skip',
  ) as CoverageRow[] | undefined;

  useEffect(() => {
    if (rows) onRows(rows);
  }, [rows, onRows]);

  return null;
}

/**
 * `useQuery` re-throws a Convex error during render. Without this the whole
 * library screen would be replaced by the root error surface because one
 * secondary column could not be read.
 */
class CoverageBoundary extends Component<
  { children: ReactNode; onError: () => void },
  { failed: boolean }
> {
  override state = { failed: false };

  static getDerivedStateFromError(): { failed: boolean } {
    return { failed: true };
  }

  override componentDidCatch(error: unknown): void {
    // Keys, not values (V3I-INV-12) — asset ids are not logged as payload.
    console.error('[AnalysisCoverage] coverage query failed', error);
    this.props.onError();
  }

  override render(): ReactNode {
    if (this.state.failed) return null;
    return this.props.children;
  }
}

export interface AssetCoverageProviderProps {
  /** The ids actually on screen — a PAGE, never the whole library. */
  assetIds: string[];
  children: ReactNode;
}

export function AssetCoverageProvider({
  assetIds,
  children,
}: AssetCoverageProviderProps): React.ReactElement {
  const { toast } = useToast();
  /**
   * Stamped with the id-set `key` it belongs to. A plain value plus a
   * "reset on key change" effect does NOT work here: React runs child effects
   * before parent effects, so the reset would fire AFTER `CoverageFetch` had
   * already published and would clobber the result on first paint. Keying the
   * state and comparing on read makes staleness derivable instead of timed.
   */
  const [entry, setEntry] = useState<{ key: string; value: CoverageMap | null } | null>(null);

  // Deduplicate and bound. `key` is a stable identity for the id set so the
  // effects below fire on a real change, not on every parent render.
  const { queried, overflow, key } = useMemo(() => {
    const unique: string[] = [];
    const seen = new Set<string>();
    for (const id of assetIds) {
      if (seen.has(id)) continue;
      seen.add(id);
      unique.push(id);
    }
    return {
      queried: unique.slice(0, COVERAGE_BATCH_MAX),
      overflow: new Set(unique.slice(COVERAGE_BATCH_MAX)),
      key: unique.join(','),
    };
  }, [assetIds]);

  const onRows = React.useCallback(
    (rows: CoverageRow[]) => {
      setEntry({ key, value: new Map(rows.map((r) => [String(r.assetId), r])) });
    },
    [key],
  );

  const onError = React.useCallback(() => {
    setEntry({ key, value: null });
    toast({
      variant: 'error',
      message: 'No se pudo leer el estado de análisis.',
      detail: 'Los videos siguen listados; su cobertura no se pudo calcular.',
    });
  }, [key, toast]);

  const value = useMemo<CoverageContextValue>(() => {
    // Nothing to ask about: an empty page is a real answer, not a pending one.
    if (queried.length === 0) return { byAsset: new Map(), overflow };
    // A result stamped with a different id set is STALE, i.e. still loading for
    // this one — never shown as this page's coverage.
    if (!entry || entry.key !== key) return { byAsset: undefined, overflow };
    return { byAsset: entry.value, overflow };
  }, [entry, key, overflow, queried.length]);

  return (
    <CoverageContext.Provider value={value}>
      {/* Remount per id set so a failure on one page does not stick to the next. */}
      <CoverageBoundary key={key} onError={onError}>
        <CoverageFetch assetIds={queried} onRows={onRows} />
      </CoverageBoundary>
      {children}
    </CoverageContext.Provider>
  );
}

type BadgeStatus =
  | { kind: 'loading' }
  | { kind: 'unavailable'; why: string }
  | { kind: 'missing' }
  | { kind: 'known'; coverage: AssetCoverage };

/** Resolve one asset against the provider — the only lookup consumers need. */
export function useAssetCoverage(assetId: string): BadgeStatus {
  const { byAsset, overflow } = useContext(CoverageContext);
  if (overflow.has(assetId)) {
    return {
      kind: 'unavailable',
      why: `Esta página supera el máximo de ${COVERAGE_BATCH_MAX} videos por consulta de cobertura.`,
    };
  }
  if (byAsset === null) {
    return { kind: 'unavailable', why: 'La consulta de cobertura falló.' };
  }
  if (byAsset === undefined) return { kind: 'loading' };
  const row = byAsset.get(assetId);
  // The id was queried but is absent from the answer, or the asset is gone.
  if (!row || !row.exists || !row.coverage) return { kind: 'missing' };
  return { kind: 'known', coverage: row.coverage };
}

/* ------------------------------------------------------------------ *
 * Per-asset badge
 * ------------------------------------------------------------------ */

export function AssetCoverageBadge({ assetId }: { assetId: string }): React.ReactElement {
  const status = useAssetCoverage(assetId);

  if (status.kind === 'loading') {
    return (
      <span data-testid="coverage-badge" data-coverage="loading">
        <Skeleton w={72} h={18} r={999} />
      </span>
    );
  }

  if (status.kind === 'unavailable') {
    return (
      <span data-testid="coverage-badge" data-coverage="unavailable" title={status.why}>
        <Pill tone="neutral" size="sm">Cobertura no disponible</Pill>
      </span>
    );
  }

  if (status.kind === 'missing') {
    return (
      <span
        data-testid="coverage-badge"
        data-coverage="missing"
        title="El backend no devolvió cobertura para este video."
      >
        <Pill tone="neutral" size="sm">Sin datos</Pill>
      </span>
    );
  }

  const { coverage } = status;
  const p = coveragePresentationFor(coverage);
  const failure = failureText(coverage.latestFailure);
  // A success that FOLLOWED a failure keeps the failure visible in the tooltip
  // rather than erasing it — the asset is fine now, the attempt still failed.
  const tooltip = failure
    ? `${p.hint}\nMotivo del fallo: ${failure}`
    : p.hint;

  return (
    <span
      data-testid="coverage-badge"
      data-coverage={coverage.state}
      data-searchable={
        coverage.isSearchable === undefined ? 'unknown' : String(coverage.isSearchable)
      }
      data-has-failure={coverage.latestFailure ? 'true' : 'false'}
      title={tooltip}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
    >
      {/* MA-82: an asset covered by a v2 run shows its four words instead of
          one state; a v1 analysis (no modalities) keeps the single pill. */}
      {coverage.modalities ? <ModalityChips modalities={coverage.modalities} /> : <Pill tone={p.tone} size="sm">{p.label}</Pill>}
      {coverage.state === 'failed' && failure && (
        <span
          data-testid="coverage-failure-reason"
          className="t-mono"
          style={{
            fontSize: 10,
            color: 'var(--muted-slate)',
            maxWidth: 150,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {failure}
        </span>
      )}
      {coverage.state !== 'failed' && coverage.latestFailure && (
        // A previous attempt failed even though the current state is fine.
        <span
          data-testid="coverage-prior-failure"
          title={`Un intento anterior falló: ${failure}`}
          className="t-mono"
          style={{ fontSize: 10, color: 'var(--warn)' }}
        >
          !
        </span>
      )}
    </span>
  );
}

/* ------------------------------------------------------------------ *
 * Asset-detail row (same data, full failure reason, no truncation)
 * ------------------------------------------------------------------ */

/**
 * The asset-detail variant. Mounted inside its own single-asset provider so the
 * detail screen does not need to know about the batch shape.
 */
export function AssetCoverageDetail({ assetId }: { assetId: string }): React.ReactElement {
  const ids = useMemo(() => [assetId], [assetId]);
  return (
    <AssetCoverageProvider assetIds={ids}>
      <AssetCoverageDetailBody assetId={assetId} />
    </AssetCoverageProvider>
  );
}

function AssetCoverageDetailBody({ assetId }: { assetId: string }): React.ReactElement {
  const status = useAssetCoverage(assetId);
  const failure = status.kind === 'known' ? failureText(status.coverage.latestFailure) : null;

  return (
    <div
      data-testid="coverage-detail"
      style={{ padding: '10px 0', borderBottom: '1px solid var(--lightest-gray)' }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
        <span style={{ fontSize: 14 }}>Cobertura de análisis</span>
        <AssetCoverageBadge assetId={assetId} />
      </div>
      {status.kind === 'known' && (
        <div style={{ marginTop: 6, fontSize: 12, color: 'var(--muted-slate)' }}>
          {coveragePresentationFor(status.coverage).hint}
          {status.coverage.attemptCount > 0 && (
            <>
              {' '}
              <span className="t-mono">
                ({status.coverage.attemptCount} intento(s))
              </span>
            </>
          )}
        </div>
      )}
      {failure && (
        <div
          data-testid="coverage-detail-failure"
          style={{ marginTop: 6, fontSize: 12, color: 'var(--bad, #DC2626)' }}
        >
          Motivo del fallo: {failure}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Fleet counts
 * ------------------------------------------------------------------ */

const FLEET_ORDER: AssetCoverageState[] = [
  'enriched',
  'stage1_complete',
  'analysing',
  'queued',
  'failed',
  'not_analysed',
];

function FleetCoverageError(): React.ReactElement {
  const { toast } = useToast();
  useEffect(() => {
    toast({
      variant: 'error',
      message: 'No se pudo leer la cobertura de la biblioteca.',
      detail: 'No se muestran cifras porque no se recibieron.',
    });
  }, [toast]);
  return (
    <Panel pad={16} style={{ marginBottom: 20 }}>
      <EmptyState
        icon="alertTriangle"
        title="Cobertura no disponible"
        body="La consulta de cobertura falló. Esto NO significa que no haya videos analizados — no se pudo calcular."
      />
    </Panel>
  );
}

class FleetBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  override state = { failed: false };
  static getDerivedStateFromError(): { failed: boolean } {
    return { failed: true };
  }
  override componentDidCatch(error: unknown): void {
    console.error('[AnalysisCoverage] fleet coverage query failed', error);
  }
  override render(): ReactNode {
    if (this.state.failed) return <FleetCoverageError />;
    return this.props.children;
  }
}

/**
 * NMW-1214 — the divergence sentence.
 *
 * `counts.enriched` is how many assets HAVE enrichment; `searchableAssets` is
 * how many of them `search_content` can actually return. When those differ,
 * BOTH are stated: reporting only the larger one is the overclaim this whole
 * component exists to prevent. When the backend does not report the searchable
 * count at all, that is said too — it is never assumed to equal the enriched
 * one.
 */
export function searchableLine(fleet: FleetCoverage): string {
  const enriched = fleet.counts?.enriched;
  const searchable = fleet.searchableAssets;

  if (typeof searchable !== 'number') {
    return 'No se informó cuántos videos son buscables por contenido. «Enriquecido» no garantiza que la búsqueda por diálogo los devuelva.';
  }

  const floor =
    (fleet.searchProbeTruncatedAssets ?? 0) > 0
      ? ` En ${fleet.searchProbeTruncatedAssets} video(s) no se pudo confirmar, así que esta cifra es un mínimo.`
      : '';

  if (typeof enriched !== 'number') {
    return `${searchable} video(s) son buscables por contenido (tienen texto indexado).${floor}`;
  }

  if (searchable < enriched) {
    const gap = fleet.enrichedNotSearchable ?? enriched - searchable;
    return `${enriched} video(s) analizados, pero solo ${searchable} son buscables por contenido: ${gap} tienen análisis sin texto indexado y la búsqueda por diálogo no los devuelve.${floor}`;
  }

  return `${enriched} video(s) analizados y los ${searchable} son buscables por contenido.${floor}`;
}

/**
 * Fleet counts strip. This is what turns "the search only covers a subset" into
 * a number.
 *
 * `getFleetCoverage` counts EXACTLY, but over a bounded newest-first window —
 * Convex has no count aggregate, so a whole-table scan is not on the table. The
 * query reports `scannedAssets` and `truncated`; this component renders BOTH.
 * Presenting a windowed number as a library total would be exactly the
 * fabricated result the contract forbids, so the wording changes when
 * `truncated` is true and never claims more than was counted.
 */
export function FleetCoverageStrip(): React.ReactElement {
  return (
    <FleetBoundary>
      <FleetCoverageStripInner />
    </FleetBoundary>
  );
}

function FleetCoverageStripInner(): React.ReactElement {
  const fleet = useQuery(api.analysis.queries.getFleetCoverage, {} as never) as
    | FleetCoverage
    | undefined;

  if (fleet === undefined) {
    return (
      <Panel pad={16} style={{ marginBottom: 20 }}>
        <div data-testid="fleet-coverage-skeleton" style={{ display: 'flex', gap: 18, alignItems: 'center' }}>
          <Skeleton w={120} h={14} r={4} />
          <Skeleton w={80} h={14} r={4} />
          <Skeleton w={80} h={14} r={4} />
          <Skeleton w={80} h={14} r={4} />
        </div>
      </Panel>
    );
  }

  if (fleet.scannedAssets === 0) {
    // An empty WINDOW is not an empty LIBRARY. The backend slices the newest N
    // assets and only then drops trashed ones, so a window that is entirely
    // trashed returns scannedAssets: 0 while live assets exist beyond it.
    // Saying "there are no videos" there is the absent-metadata-read-as-
    // absent-content error this whole surface exists to prevent.
    return (
      <Panel pad={16} style={{ marginBottom: 20 }}>
        <div data-testid="fleet-coverage-empty" style={{ fontSize: 13, color: 'var(--muted-slate)' }}>
          {fleet.truncated
            ? 'La ventana analizada (los más recientes) no contiene videos activos, pero la biblioteca tiene más allá de ella. No se puede informar cobertura desde aquí.'
            : 'No hay videos en la biblioteca, así que no hay cobertura que informar.'}
        </div>
      </Panel>
    );
  }

  const scope = fleet.truncated
    ? `los ${fleet.scannedAssets} videos más recientes`
    : `los ${fleet.scannedAssets} videos de la biblioteca`;

  return (
    <Panel pad={16} style={{ marginBottom: 20 }}>
      <div data-testid="fleet-coverage" style={{ display: 'flex', flexWrap: 'wrap', gap: 18, alignItems: 'center' }}>
        <span className="t-mono" style={{ fontSize: 11, color: 'var(--muted-slate)' }}>
          Cobertura de análisis
        </span>
        {FLEET_ORDER.map((state) => {
          const n = fleet.counts?.[state];
          // A count the backend did not send is not zero. Skip it rather than
          // render a number we did not receive.
          if (typeof n !== 'number') return null;
          const p = coveragePresentation(state);
          return (
            <span
              key={state}
              data-testid={`fleet-count-${state}`}
              title={p.hint}
              style={{ display: 'inline-flex', alignItems: 'baseline', gap: 6 }}
            >
              <span
                style={{
                  fontFamily: 'var(--font-display)',
                  fontSize: 20,
                  fontWeight: 400,
                  letterSpacing: '-0.4px',
                }}
              >
                {n}
              </span>
              <span style={{ fontSize: 11, color: 'var(--muted-slate)' }}>{p.label}</span>
            </span>
          );
        })}
        {typeof fleet.searchableAssets === 'number' && (
          <span
            data-testid="fleet-count-searchable"
            title="Videos que la búsqueda por contenido puede devolver realmente: tienen texto indexado. Siempre <= enriquecidos."
            style={{ display: 'inline-flex', alignItems: 'baseline', gap: 6 }}
          >
            <span
              style={{
                fontFamily: 'var(--font-display)',
                fontSize: 20,
                fontWeight: 400,
                letterSpacing: '-0.4px',
              }}
            >
              {fleet.searchableAssets}
            </span>
            <span style={{ fontSize: 11, color: 'var(--muted-slate)' }}>Buscables</span>
          </span>
        )}
      </div>
      <div
        data-testid="fleet-coverage-searchable"
        style={{ marginTop: 8, fontSize: 12, color: 'var(--body-muted)' }}
      >
        {searchableLine(fleet)}
      </div>
      <div
        data-testid="fleet-coverage-scope"
        style={{ marginTop: 8, fontSize: 12, color: 'var(--body-muted)' }}
      >
        {fleet.truncated
          ? `Contado sobre ${scope} — la biblioteca tiene más, así que estas cifras NO son el total.`
          : `Contado sobre ${scope}.`}
      </div>
      {fleet.assetsWithAnyFailure > 0 && (
        <div
          data-testid="fleet-coverage-failures"
          style={{ marginTop: 8, fontSize: 12, color: 'var(--body-muted)' }}
        >
          {/* Deliberately not equal to counts.failed: an asset that failed once
              and later succeeded is counted here and not there. */}
          {fleet.assetsWithAnyFailure} video(s) tienen al menos un intento fallido
          {fleet.failureReasons.length > 0 && ' — '}
          {fleet.failureReasons.map((f, i) => (
            <span key={f.reason} data-testid="fleet-failure-reason">
              {i > 0 && ', '}
              <span className="t-mono">{f.reason}</span> ×{f.count}
            </span>
          ))}
          .
        </div>
      )}
    </Panel>
  );
}
